// Contabilidad básica: gastos, caja, resultado mensual y cuentas por pagar. Diseño: docs/estrategia/PROJECT_STATE.md.
// Dos cifras que no se mezclan:
//   · Caja (base de caja): lo cobrado en el mes (abonos vigentes por su fecha de pago) menos lo pagado en el mes (gastos pagados por su fecha de pago).
//   · Resultado (base de causación): lo vendido en el mes (ventas no anuladas) menos los gastos del mes (pagados o por pagar, no anulados) por su fecha.
// Todo en centavos enteros; el contador recibe las dos en el CSV.
import { addDaysStr, localDateStr } from './customers';
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS, csvAmount, csvDate, csvText, fromCents, toCents, toCsv } from './sales';

export const EXPENSE_CATEGORIES = ['Arriendo', 'Servicios públicos', 'Nómina y honorarios', 'Proveedores y mercancía', 'Insumos', 'Marketing y publicidad', 'Transporte y domicilios', 'Impuestos', 'Software y tecnología', 'Mantenimiento', 'Otros'];
export const EXPENSE_METHODS = PAYMENT_METHODS;
export const EXPENSE_STATUS_LABELS = { pagado: 'Pagado', por_pagar: 'Por pagar', anulado: 'Anulado' };

const pad2 = (n) => String(n).padStart(2, '0');
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// "2026-10" del día dado (hora local).
export const monthKey = (day = localDateStr()) => String(day).slice(0, 7);
export const addMonths = (ym, n) => {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${pad2((idx % 12) + 1)}`;
};
export const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
export const monthShort = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1].slice(0, 3)} ${String(y).slice(2)}`; };
export const lastMonths = (endYm, count) => Array.from({ length: count }, (_, i) => addMonths(endYm, i - (count - 1)));
// Primer día del mes y primer día del mes siguiente (el rango es [from, nextFrom)).
export const monthRange = (ym) => ({ from: `${ym}-01`, nextFrom: `${addMonths(ym, 1)}-01` });

// Día local (YYYY-MM-DD) de una fecha o marca de tiempo; las fechas sin hora se dejan tal cual.
const dayOf = (v) => {
  if (!v) return '';
  const raw = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : localDateStr(d);
};

const isLiveExpense = (e) => e && e.status !== 'anulado';
const isLivePayment = (p) => p && !p.voided_at;
const isLiveSale = (s) => s && s.status !== 'anulada';

// Cifras de un mes. `payments`, `sales` y `expenses` pueden traer más meses: aquí solo cuenta el pedido.
export const monthSummary = ({ sales = [], payments = [], expenses = [] }, ym) => {
  let sold = 0; let collected = 0; let paidOut = 0; let accrued = 0;
  const byCategory = new Map();
  const collectedByMethod = new Map();
  const paidByMethod = new Map();
  sales.filter(isLiveSale).forEach((s) => { if (monthKey(dayOf(s.created_at)) === ym) sold += toCents(s.total); });
  payments.filter(isLivePayment).forEach((p) => {
    if (monthKey(dayOf(p.paid_at)) !== ym) return;
    const c = toCents(p.amount);
    collected += c;
    collectedByMethod.set(p.method, (collectedByMethod.get(p.method) || 0) + c);
  });
  expenses.filter(isLiveExpense).forEach((e) => {
    const c = toCents(e.amount);
    if (monthKey(dayOf(e.expense_date)) === ym) {
      accrued += c;
      byCategory.set(e.category, (byCategory.get(e.category) || 0) + c);
    }
    if (e.status === 'pagado' && monthKey(dayOf(e.paid_at)) === ym) {
      paidOut += c;
      paidByMethod.set(e.payment_method, (paidByMethod.get(e.payment_method) || 0) + c);
    }
  });
  const list = (map, labels) => [...map.entries()].map(([key, cents]) => ({ key, label: labels?.[key] || key, cents })).sort((a, b) => b.cents - a.cents);
  return {
    month: ym,
    sold: fromCents(sold), accrued: fromCents(accrued), result: fromCents(sold - accrued),
    collected: fromCents(collected), paidOut: fromCents(paidOut), cash: fromCents(collected - paidOut),
    byCategory: list(byCategory).map((r) => ({ ...r, amount: fromCents(r.cents), share: accrued ? r.cents / accrued : 0 })),
    collectedByMethod: list(collectedByMethod, PAYMENT_METHOD_LABELS).map((r) => ({ ...r, amount: fromCents(r.cents) })),
    paidByMethod: list(paidByMethod, PAYMENT_METHOD_LABELS).map((r) => ({ ...r, amount: fromCents(r.cents) }))
  };
};

// Serie de los últimos `count` meses terminando en `endYm` (para la tabla comparativa).
export const monthSeries = (data, endYm, count = 6) => lastMonths(endYm, count).map((ym) => {
  const s = monthSummary(data, ym);
  return { month: ym, sold: s.sold, accrued: s.accrued, result: s.result, collected: s.collected, paidOut: s.paidOut, cash: s.cash };
});

// Saldo de caja acumulado hasta el fin de un mes (todo lo cobrado menos todo lo pagado hasta entonces). Solo es válido si `payments` y `expenses`
// traen TODO el historial; la pantalla lo muestra solo cuando se cargó completo.
export const cashBalanceUntil = ({ payments = [], expenses = [] }, ym) => {
  const { nextFrom } = monthRange(ym);
  let cents = 0;
  payments.filter(isLivePayment).forEach((p) => { if (dayOf(p.paid_at) < nextFrom) cents += toCents(p.amount); });
  expenses.filter((e) => e.status === 'pagado').forEach((e) => { if (dayOf(e.paid_at) < nextFrom) cents -= toCents(e.amount); });
  return fromCents(cents);
};

// ---- Cuentas por pagar ----
export const isPayable = (e) => e?.status === 'por_pagar';

export const payableDays = (e, today = localDateStr()) => {
  if (!isPayable(e) || !e.due_date) return 0;
  const a = String(e.due_date).slice(0, 10).split('-').map(Number);
  const b = String(today).split('-').map(Number);
  return Math.max(0, Math.floor((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86400000));
};
export const isPayableOverdue = (e, today = localDateStr()) => isPayable(e) && Boolean(e.due_date) && payableDays(e, today) > 0;

// Por pagar: total, vencido y lo que vence en los próximos 7 días; lista ordenada por vencimiento (sin fecha al final).
export const payablesSummary = (expenses, today = localDateStr()) => {
  const rows = (expenses || []).filter(isPayable).sort((a, b) => (a.due_date || '9999-12-31').localeCompare(b.due_date || '9999-12-31') || String(a.created_at).localeCompare(String(b.created_at)));
  const soonLimit = addDaysStr(7, new Date(`${today}T12:00:00`));
  let total = 0; let overdue = 0; let soon = 0;
  rows.forEach((e) => {
    const c = toCents(e.amount);
    total += c;
    if (isPayableOverdue(e, today)) overdue += c;
    else if (e.due_date && String(e.due_date).slice(0, 10) <= soonLimit) soon += c;
  });
  return { rows, total: fromCents(total), overdue: fromCents(overdue), soon: fromCents(soon), count: rows.length };
};

// Por cobrar al día de hoy (ventas abiertas): el saldo de lo que clientes deben.
export const receivablesTotal = (openSales) => fromCents((openSales || []).reduce((acc, s) => (s.status === 'anulada' ? acc : acc + toCents(s.total) - toCents(s.paid_total)), 0));

// Comprobación previa de un gasto en pantalla (la base vuelve a validar todo).
export const checkExpense = (form, today = localDateStr()) => {
  const amount = Number(form.amount);
  if (!String(form.description ?? '').trim()) return 'Escribe qué fue el gasto.';
  if (!String(form.category ?? '').trim()) return 'Elige la categoría.';
  if (form.amount === '' || form.amount == null || !Number.isFinite(amount)) return 'Escribe el valor del gasto.';
  if (Math.abs(amount * 100 - Math.round(amount * 100)) > 1e-6) return 'El valor admite hasta 2 decimales.';
  if (amount <= 0) return 'El valor debe ser mayor que 0.';
  if (!form.expense_date) return 'Indica la fecha del gasto.';
  if (form.expense_date > today) return 'La fecha del gasto no puede ser futura.';
  if (form.status === 'pagado') {
    if (!form.payment_method) return 'Indica cómo se pagó.';
    if (form.paid_at && form.paid_at > today) return 'La fecha de pago no puede ser futura.';
  }
  return null;
};

// Fila para insertar (la base completa quién lo registró y normaliza según el estado).
export const buildExpenseRow = (form, businessId) => {
  const paid = form.status === 'pagado';
  const clean = (v) => { const t = String(v ?? '').trim(); return t || null; };
  return {
    business_id: String(businessId),
    expense_date: form.expense_date,
    category: String(form.category).trim(),
    description: String(form.description).trim(),
    supplier: clean(form.supplier),
    amount: Math.round(Number(form.amount) * 100) / 100,
    status: paid ? 'pagado' : 'por_pagar',
    payment_method: paid ? form.payment_method : null,
    paid_at: paid ? (form.paid_at || form.expense_date) : null,
    due_date: paid ? null : (form.due_date || null),
    reference: clean(form.reference),
    notes: clean(form.notes)
  };
};

// ---- CSV para el contador ----
export const expensesToCsv = (expenses) =>
  toCsv(
    ['Fecha', 'Categoría', 'Descripción', 'Proveedor', 'Referencia', 'Valor', 'Estado', 'Medio de pago', 'Fecha de pago', 'Vence', 'Notas', 'Motivo de anulación'],
    (expenses || []).map((e) => [
      csvDate(e.expense_date), e.category, e.description, e.supplier || '', e.reference || '', csvAmount(e.amount), EXPENSE_STATUS_LABELS[e.status] || e.status,
      PAYMENT_METHOD_LABELS[e.payment_method] || '', csvDate(e.paid_at), csvDate(e.due_date), e.notes || '', e.void_reason || ''
    ].map(csvText))
  );

// Resultado mensual: una fila por mes con las dos bases (caja y causación).
export const monthlyToCsv = (series) =>
  toCsv(
    ['Mes', 'Ventas del mes', 'Gastos del mes', 'Utilidad del mes', 'Cobrado', 'Gastos pagados', 'Caja del mes'],
    [
      ...(series || []).map((r) => [r.month, csvAmount(r.sold), csvAmount(r.accrued), csvAmount(r.result), csvAmount(r.collected), csvAmount(r.paidOut), csvAmount(r.cash)].map(csvText)),
      ['TOTAL', ...['sold', 'accrued', 'result', 'collected', 'paidOut', 'cash'].map((k) => csvAmount(fromCents((series || []).reduce((acc, r) => acc + toCents(r[k]), 0))))].map(csvText)
    ]
  );

// Movimientos de caja de un mes (cada abono cobrado y cada gasto pagado) en orden de fecha, con el saldo corrido del mes.
export const cashMovements = ({ payments = [], expenses = [], salesById = new Map() }, ym) => {
  const moves = [];
  payments.filter(isLivePayment).forEach((p) => {
    if (monthKey(dayOf(p.paid_at)) !== ym) return;
    moves.push({ date: dayOf(p.paid_at), kind: 'Ingreso', concept: `Cobro venta ${salesById.get(p.sale_id)?.number || ''}`.trim(), method: p.method, cents: toCents(p.amount) });
  });
  expenses.filter((e) => e.status === 'pagado').forEach((e) => {
    if (monthKey(dayOf(e.paid_at)) !== ym) return;
    moves.push({ date: dayOf(e.paid_at), kind: 'Egreso', concept: `${e.category}: ${e.description}${e.supplier ? ` (${e.supplier})` : ''}`, method: e.payment_method, cents: -toCents(e.amount) });
  });
  moves.sort((a, b) => a.date.localeCompare(b.date) || (a.cents > 0 ? -1 : 1));
  let running = 0;
  return moves.map((m) => { running += m.cents; return { ...m, amount: fromCents(m.cents), balance: fromCents(running) }; });
};

export const cashToCsv = (moves) =>
  toCsv(
    ['Fecha', 'Tipo', 'Concepto', 'Medio', 'Valor', 'Saldo del mes'],
    (moves || []).map((m) => [csvDate(m.date), m.kind, m.concept, PAYMENT_METHOD_LABELS[m.method] || m.method || '', csvAmount(m.amount), csvAmount(m.balance)].map(csvText))
  );
